import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../../hooks/hooks";
import { fetchAxiosNoToken } from "../../../helpers/fetchAxiosNoToken";
import { set_landing_house } from "../../../redux/reducers/landingReducer";
import { fetchAxiosToken } from "../../../helpers/fetchAxiosToken";
import { NavbarsAvatar } from "../../../components/NavbarsAvatar";
import { ModalLogin } from "../../ModalLogin";
import { formatearNumero } from "../../../components/helpers/formatearNumero";

interface Pedido {
    id: number;
    id_cliente: number;
    id_habitacion: number;
    costo_alquiler: string;
    garantia: string;
    tipo_garantia: string | null;
    descuento_estadia: string;
    descuento_cupon: string;
    costo_servicios_adicionales: string;
    costo_total: string;
    pagado: string;
    saldo: string;
    habilitado: string;
    estado:
        | "Confirmar disponibilidad"
        | "Pendiente de pago"
        | "Pago Parcial"
        | "Pago Completo"
        | string;
    fecha_inicio: string;
    fecha_final: string;
    tipo_moneda: string;
    fecha_registro: string;
    adultos: number;
    ninos: number;
    oferta: string | null;
    expiracion: any;
    pedidos_servicios_adicionales: {
        id: number;
        id_pedido: any;
        id_pedido_habitaciones: number;
        servicio: string;
        costo: string;
    }[];
    HotelHabitaciones: {
        id: number;
        nombre: string;
        banner: string;
        casa: any;
    };
    Cliente: Cliente | null;

    pedidos_tours: Tour[];
}

export interface Tour {
    id: number;
    id_tour: number;
    id_pedido_habitaciones: number;
    hora_inicio: string;
    hora_final: string;
    precio_simple: string;
    numero_adultos: number;
    numero_ninos: number;
    precio_tour: string;
    fecha: string;
    hotel_tour: HotelTour;
}
export interface HotelTour {
    id: number;
    nombre: string;
    banner: string;
    casa: Casa;
}
export interface Casa {
    id: number;
    nombre: string;
    banner: string;
    Propietario: Propietario;
}
export interface Propietario {
    id: number;
    nombre: string;
    apellido: string;
    avatar: string;
}
export interface Cliente {
    id: number;
    nombre: string;
    apellido: string;
    avatar: string;
    correo: string;
    datos_facturacion_receptore: DatosFacturacionReceptore;
}
type TipoComprobante = "Factura" | "Boleta";

type TipoDocSunat =
    | "DOC_TRIB_NO_DOM_SIN_RUC"
    | "DOC_NACIONAL_DE_IDENTIDAD"
    | "CARNET_DE_EXTRANJERIA"
    | "RUC"
    | "PASAPORTE"
    | "CED_DIPLOMATICA_IDENTIDAD";

type FacturacionForm = {
    tipo_comprobante: TipoComprobante;
    correo: string;
    correo_copia?: string;
    direccion: string;
    ubigeo: string;
    nombre_comercial: string;
    nombre_legal: string;
    tipo_documento_identidad: TipoDocSunat;
    numero_documento_identidad: string;
};

export interface DatosFacturacionReceptore {
    id: number;
    id_usuario: string;
    correo: string;
    correo_copia: string;
    direccion: string;
    ubigeo: string;
    nombre_comercial: string;
    nombre_legal: string;
    numero_documento_identidad: string;
    tipo_documento_identidad: string;
}

const DOC_OPCIONES: { value: TipoDocSunat; label: string }[] = [
    {
        value: "DOC_TRIB_NO_DOM_SIN_RUC",
        label: "DOC_TRIB_NO_DOM_SIN_RUC (Extranjero sin RUC)",
    },
    {
        value: "DOC_NACIONAL_DE_IDENTIDAD",
        label: "DOC_NACIONAL_DE_IDENTIDAD (DNI)",
    },
    { value: "CARNET_DE_EXTRANJERIA", label: "CARNET_DE_EXTRANJERIA" },
    // { value: "RUC", label: "RUC" },
    { value: "PASAPORTE", label: "PASAPORTE" },
    { value: "CED_DIPLOMATICA_IDENTIDAD", label: "CED_DIPLOMATICA_IDENTIDAD" },
];

const DOC_OPCIONES_FACTURA: { value: TipoDocSunat; label: string }[] = [
    // {
    //     value: "DOC_NACIONAL_DE_IDENTIDAD",
    //     label: "DOC_NACIONAL_DE_IDENTIDAD (DNI)",
    // },

    { value: "RUC", label: "RUC" },

    // { value: "CED_DIPLOMATICA_IDENTIDAD", label: "CED_DIPLOMATICA_IDENTIDAD" },
];

const DOC_EXTRANJERO_SIN_IGV: TipoDocSunat[] = [
    "DOC_TRIB_NO_DOM_SIN_RUC",
    "CARNET_DE_EXTRANJERIA",
    "PASAPORTE",
    "CED_DIPLOMATICA_IDENTIDAD",
];

const CONTROL =
    "w-full h-10 px-3 rounded-lg border border-[#E9E9E9] bg-white outline-none text-sm focus:ring-2 focus:ring-[#1C4CBB]/40 focus:border-[#1C4CBB]";

const ModalDatosComprobante: React.FC<{
    visible: boolean;
    onClose: () => void;
    initial?: Partial<FacturacionForm>;
    onSave: (data: FacturacionForm) => void;
}> = ({ visible, onClose, initial, onSave }) => {
    const [form, setForm] = useState<FacturacionForm>({
        tipo_comprobante: "Factura",
        correo: "",
        correo_copia: "",
        direccion: "",
        ubigeo: "",
        nombre_comercial: "",
        nombre_legal: "",
        tipo_documento_identidad: "RUC",
        numero_documento_identidad: "",
        ...initial,
    });

    useEffect(() => {
        if (visible && initial) {
            setForm((p) => ({ ...p, ...initial }));
        }
    }, [visible, initial]);

    const EMAIL = /^\S+@\S+\.\S+$/;
    const onlyDigits = (s: string) => s.replace(/\D/g, "");

    const validate = () => {
        if (!form.tipo_comprobante)
            return "Debes seleccionar Factura o Boleta.";
        if (!EMAIL.test(form.correo)) return "Correo inválido.";
        if (form.correo_copia && !EMAIL.test(form.correo_copia))
            return "Correo copia inválido.";
        if (!form.direccion.trim()) return "Dirección requerida.";
        if (!form.ubigeo.trim()) return "Ubigeo requerido.";
        if (!form.nombre_comercial.trim()) return "Nombre comercial requerido.";
        if (!form.nombre_legal.trim()) return "Nombre legal requerido.";
        if (!form.tipo_documento_identidad)
            return "Selecciona el tipo de documento.";
        const ndoc = form.numero_documento_identidad.trim();

        switch (form.tipo_documento_identidad) {
            case "RUC":
                if (!/^\d{11}$/.test(ndoc)) return "RUC inválido (11 dígitos).";
                break;
            case "DOC_NACIONAL_DE_IDENTIDAD":
                if (!/^\d{8}$/.test(ndoc)) return "DNI inválido (8 dígitos).";
                break;
            default:
                if (!/^[A-Za-z0-9\-]{6,}$/.test(ndoc))
                    return "Número de documento inválido (mín. 6 caracteres).";
        }
        return null;
    };

    if (!visible) return null;

    return (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-2xl p-4 bg-white rounded-xl">
                <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold">
                        Datos para el comprobante
                    </h3>
                    <button
                        className="px-3 py-1 text-sm text-[#1C4CBB] border border-[#1C4CBB] rounded-lg"
                        onClick={onClose}>
                        Cerrar
                    </button>
                </div>
                <hr className="my-3" />

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">
                            Tipo de comprobante *
                        </label>
                        <select
                            className={CONTROL}
                            value={form.tipo_comprobante}
                            onChange={(e) =>
                                setForm((p) => ({
                                    ...p,
                                    tipo_comprobante: e.target
                                        .value as TipoComprobante,
                                    tipo_documento_identidad:
                                        e.target.value === "Boleta"
                                            ? "DOC_TRIB_NO_DOM_SIN_RUC"
                                            : "RUC",
                                }))
                            }>
                            <option value="Factura">Factura</option>
                            <option value="Boleta">Boleta</option>
                        </select>
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">
                            Tipo de documento *
                        </label>
                        <select
                            className={CONTROL}
                            value={form.tipo_documento_identidad}
                            onChange={(e) =>
                                setForm((p) => ({
                                    ...p,
                                    tipo_documento_identidad: e.target
                                        .value as TipoDocSunat,

                                    numero_documento_identidad:
                                        e.target.value === "RUC"
                                            ? onlyDigits(
                                                  p.numero_documento_identidad
                                              ).slice(0, 11)
                                            : p.numero_documento_identidad,
                                }))
                            }>
                            {form.tipo_comprobante === "Factura"
                                ? DOC_OPCIONES_FACTURA.map((o) => (
                                      <option key={o.value} value={o.value}>
                                          {o.label}
                                      </option>
                                  ))
                                : DOC_OPCIONES.map((o) => (
                                      <option key={o.value} value={o.value}>
                                          {o.label}
                                      </option>
                                  ))}
                        </select>
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">
                            {form.tipo_documento_identidad === "RUC"
                                ? "RUC *"
                                : "Nro. documento *"}
                        </label>
                        <input
                            className={CONTROL}
                            value={form.numero_documento_identidad}
                            onChange={(e) => {
                                const v = e.target.value;
                                if (form.tipo_documento_identidad === "RUC") {
                                    return setForm((p) => ({
                                        ...p,
                                        numero_documento_identidad: v
                                            .replace(/\D/g, "")
                                            .slice(0, 11),
                                    }));
                                }
                                setForm((p) => ({
                                    ...p,
                                    numero_documento_identidad: v.slice(0, 20),
                                }));
                            }}
                            placeholder={
                                form.tipo_documento_identidad === "RUC"
                                    ? "2061********"
                                    : "Documento del cliente"
                            }
                            inputMode={
                                form.tipo_documento_identidad === "RUC" ||
                                form.tipo_documento_identidad ===
                                    "DOC_NACIONAL_DE_IDENTIDAD"
                                    ? "numeric"
                                    : "text"
                            }
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">Ubigeo *</label>
                        <input
                            className={CONTROL}
                            value={form.ubigeo}
                            onChange={(e) =>
                                setForm((p) => ({
                                    ...p,
                                    ubigeo: e.target.value,
                                }))
                            }
                            placeholder="Ej: 591"
                        />
                    </div>

                    <div className="flex flex-col gap-1 sm:col-span-2">
                        <label className="text-sm font-medium">
                            Dirección *
                        </label>
                        <input
                            className={CONTROL}
                            value={form.direccion}
                            onChange={(e) =>
                                setForm((p) => ({
                                    ...p,
                                    direccion: e.target.value,
                                }))
                            }
                            placeholder="Av. Siempre Viva 123"
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">
                            Nombre comercial *
                        </label>
                        <input
                            className={CONTROL}
                            value={form.nombre_comercial}
                            onChange={(e) =>
                                setForm((p) => ({
                                    ...p,
                                    nombre_comercial: e.target.value,
                                }))
                            }
                            placeholder="Mi Empresa / Nombres y Apellidos"
                        />
                    </div>

                    <div className="flex flex-col gap-1 sm:col-span-2">
                        <label className="text-sm font-medium">
                            Nombre legal *
                        </label>
                        <input
                            className={CONTROL}
                            value={form.nombre_legal}
                            onChange={(e) =>
                                setForm((p) => ({
                                    ...p,
                                    nombre_legal: e.target.value,
                                }))
                            }
                            placeholder="Razón social o Nombres y Apellidos"
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">Correo *</label>
                        <input
                            type="email"
                            className={CONTROL}
                            value={form.correo}
                            onChange={(e) =>
                                setForm((p) => ({
                                    ...p,
                                    correo: e.target.value,
                                }))
                            }
                            placeholder="facturacion@empresa.com"
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">
                            Correo copia (opcional)
                        </label>
                        <input
                            type="email"
                            className={CONTROL}
                            value={form.correo_copia ?? ""}
                            onChange={(e) =>
                                setForm((p) => ({
                                    ...p,
                                    correo_copia: e.target.value,
                                }))
                            }
                            placeholder="copia@empresa.com"
                        />
                    </div>
                </div>

                <div className="flex justify-center mt-4">
                    <button
                        className="h-10 px-6 text-white rounded-lg bg-[#1C4CBB]"
                        onClick={() => {
                            const err = validate();
                            if (err) {
                                Swal.fire({
                                    confirmButtonColor: "#029BFC",
                                    iconColor: "#029BFC",
                                    title: "Dato inválido",
                                    text: err,
                                    icon: "error",
                                });
                                return;
                            }
                            onSave(form);
                        }}>
                        Guardar datos
                    </button>
                </div>
            </div>
        </div>
    );
};

export const CheckoutPagoAdmin = () => {
    const dispatch = useDispatch();
    const { id, tipo_usuario } = useAppSelector((s) => s.usuario_logeado);
    const { house } = useAppSelector((s) => s.landing_house);
    const { id_reserva } = useParams();
    const navigate = useNavigate();

    const [pedido, setPedido] = useState<Pedido | null>(null);
    const [modalVisibleLogin, setModalVisibleLogin] = useState(false);

    const [pagoUnico, setPagoUnico] = useState(true);
    const [comprobantePreview, setComprobantePreview] = useState<string | null>(
        null
    );
    const [comprobante, setComprobante] = useState<File | null>(null);
    const [enviando, setEnviando] = useState(false);
    const [formaPago, setFormaPago] = useState("Transferencia bancaria");
    const [isComprobante, setIsComprobante] = useState(false);
    const [showModalComprobante, setShowModalComprobante] = useState(false);
    const [factData, setFactData] = useState<FacturacionForm | null>(null);

    const [montoAPagar, setMontoAPagar] = useState<number | null>(0);

    const baseTotal = useMemo(
        () => (pedido ? Number(pedido.costo_total) : 0),
        [pedido]
    );

    const isIgv = useMemo(() => {
        if (!isComprobante || !factData) return false;
        return !DOC_EXTRANJERO_SIN_IGV.includes(
            factData.tipo_documento_identidad
        );
    }, [isComprobante, factData]);

    const igv = useMemo(
        () =>
            isIgv && pedido
                ? Number(
                      ((baseTotal - Number(pedido?.garantia)) * 0.18).toFixed(2)
                  )
                : 0,
        [isIgv, baseTotal]
    );
    const totalConIgv = useMemo(
        () => Number((baseTotal + igv).toFixed(2)),
        [baseTotal, igv]
    );

    useEffect(() => {
        if (!pedido) return;
        const totalParaCalculo = isComprobante ? totalConIgv : baseTotal;
        const sugerido = Math.ceil(
            (totalParaCalculo * Number(house.porcentaje_primer_pago)) / 100
        );
        setMontoAPagar(sugerido);
    }, [
        pedido,
        isComprobante,
        totalConIgv,
        baseTotal,
        house.porcentaje_primer_pago,
    ]);

    const getHouseFromTenant = async () => {
        try {
            const data = await fetchAxiosNoToken({
                url: `/casa/getOne/dominio`,
            });
            dispatch(set_landing_house(data));
        } catch (e) {
            console.log(e);
        }
    };

    useEffect(() => {
        setModalVisibleLogin(tipo_usuario === null);
        fetchAxiosToken({
            url: `/pedidos_habitaciones/getOne/${id_reserva}/${id}`,
        }).then((data) => {
            setPedido(data);

            if (data?.Cliente?.datos_facturacion_receptore) {
                const dfr = data.Cliente.datos_facturacion_receptore;
                setFactData({
                    tipo_comprobante: dfr.tipo_comprobante || "Factura",

                    correo: dfr.correo,
                    correo_copia: dfr.correo_copia || "",
                    direccion: dfr.direccion,
                    ubigeo: dfr.ubigeo,
                    nombre_comercial: dfr.nombre_comercial,
                    nombre_legal: dfr.nombre_legal,
                    tipo_documento_identidad:
                        dfr.tipo_documento_identidad as TipoDocSunat,
                    numero_documento_identidad: dfr.numero_documento_identidad,
                });
            }
        });
        getHouseFromTenant();
    }, [tipo_usuario, id_reserva, id]);

    const handleComprobante = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        setComprobante(e.target.files[0]);
        const objectUrl = URL.createObjectURL(e.target.files[0]);
        setComprobantePreview(objectUrl);
    };

    const idCliete = () => {
        if (tipo_usuario === 8) return 0;
        if (pedido?.oferta === "No") return pedido.id_cliente;
        return id;
    };

    const mostrarOpcionComprobante = pedido && pedido.estado !== "Pago Parcial";

    const appendComprobanteToFormData = (fd: FormData) => {
        if (!isComprobante || !factData) {
            fd.append("is_igv", isComprobante ? "Si" : "No");
            return;
        }

        fd.append("tipo_comprobante", factData.tipo_comprobante);
        fd.append("is_igv", isComprobante ? "Si" : "No");

        fd.append("correo", factData.correo);
        if (factData.correo_copia)
            fd.append("correo_copia", factData.correo_copia);
        fd.append("direccion", factData.direccion);
        fd.append("ubigeo", factData.ubigeo);
        fd.append("nombre_comercial", factData.nombre_comercial);
        fd.append("nombre_legal", factData.nombre_legal);
        fd.append(
            "tipo_documento_identidad",
            factData.tipo_documento_identidad
        );
        fd.append(
            "numero_documento_identidad",
            factData.numero_documento_identidad
        );
    };
    console.log("pedido", pedido);

    const mergeComprobanteOnJson = (body: any) => {
        if (!isComprobante || !factData) {
            return { ...body, is_igv: isComprobante ? "Si" : "No" };
        }
        if (
            factData.tipo_comprobante === "Factura" &&
            !DOC_OPCIONES_FACTURA.find(
                (o) => o.value === factData.tipo_documento_identidad
            )
        ) {
            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Error en el tipo de documento",
                text: "El tipo de documento seleccionado no es válido para emitir una factura.",
                icon: "error",
            });

            throw new Error("Tipo de documento inválido para factura");
        }
        return {
            ...body,
            tipo_comprobante: factData.tipo_comprobante,
            is_igv: isComprobante ? "Si" : "No",
            correo: factData.correo,
            correo_copia: factData.correo_copia ?? "",
            direccion: factData.direccion,
            ubigeo: factData.ubigeo,
            nombre_comercial: factData.nombre_comercial,
            nombre_legal: factData.nombre_legal,
            tipo_documento_identidad: factData.tipo_documento_identidad,
            numero_documento_identidad: factData.numero_documento_identidad,
        };
    };

    const sendPayment = async () => {
        if (!comprobante) {
            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Debes adjuntar el comprobante",
                icon: "warning",
            });
            return;
        }
        if (!pedido) return;

        if (mostrarOpcionComprobante && isComprobante && !factData) {
            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Faltan datos del comprobante",
                text: "Completa los datos para emitir el comprobante.",
                icon: "warning",
            });
            return;
        }

        const esSegundoPago = pedido.estado === "Pago Parcial";
        const totalMostrar = isComprobante ? totalConIgv : baseTotal;

        if (
            !pagoUnico &&
            !esSegundoPago &&
            (!montoAPagar || montoAPagar <= 0)
        ) {
            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Debes ingresar un monto a pagar",
                icon: "warning",
            });
            return;
        }

        try {
            setEnviando(true);
            if (
                factData &&
                factData.tipo_comprobante === "Factura" &&
                isComprobante &&
                !DOC_OPCIONES_FACTURA.find(
                    (o) => o.value === factData.tipo_documento_identidad
                )
            ) {
                Swal.fire({
                    confirmButtonColor: "#029BFC",
                    iconColor: "#029BFC",
                    title: "Error en el tipo de documento",
                    text: "El tipo de documento seleccionado no es válido para emitir una factura.",
                    icon: "error",
                });
                return;
            }
            const form = new FormData();
            form.append("id_pedidos_habitaciones", `${id_reserva}`);
            form.append("id_cliente", `${idCliete()}`);

            const montoAEnviar = esSegundoPago
                ? pedido.saldo
                : pagoUnico
                ? String(totalMostrar)
                : String(montoAPagar);
            form.append("monto", `${montoAEnviar}`);
            form.append(
                "etapa_pago",
                `${pagoUnico ? "Pago Completo" : "Pago Parcial"}`
            );
            form.append("pago_pedido", comprobante);

            appendComprobanteToFormData(form);

            await fetchAxiosToken({
                url: `/pedidos_pagos_habitaciones/postPagoByAdmin/`,
                method: `post`,
                body: form,
            });

            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Pago cargado con éxito",
                icon: "success",
                confirmButtonText: "Ok",
            });
            navigate("/");
        } catch (e: any) {
            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Error",
                text:
                    e?.response?.data?.message ??
                    "No se pudo registrar el pago.",
                icon: "error",
            });
        } finally {
            setEnviando(false);
        }
    };

    const sendPaymentPorEfetivoOTarjeta = async (metodo: string) => {
        if (!pedido) return;

        if (mostrarOpcionComprobante && isComprobante && !factData) {
            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Faltan datos del comprobante",
                text: "Completa los datos para emitir el comprobante.",
                icon: "warning",
            });
            return;
        }

        try {
            setEnviando(true);
            const esSegundoPago = pedido.estado === "Pago Parcial";
            const totalMostrar = isComprobante ? totalConIgv : baseTotal;

            const montoAEnviar = esSegundoPago
                ? pedido.saldo
                : pagoUnico
                ? String(totalMostrar)
                : String(montoAPagar);

            let body: any = {
                id_pedidos_habitaciones: id_reserva,
                id_cliente: idCliete(),
                monto: `${montoAEnviar}`,
                etapa_pago: pagoUnico ? "Pago Completo" : "Pago Parcial",
                metodo,
            };
            if (
                factData &&
                factData.tipo_comprobante === "Factura" &&
                isComprobante &&
                !DOC_OPCIONES_FACTURA.find(
                    (o) => o.value === factData.tipo_documento_identidad
                )
            ) {
                Swal.fire({
                    confirmButtonColor: "#029BFC",
                    iconColor: "#029BFC",
                    title: "Error en el tipo de documento",
                    text: "El tipo de documento seleccionado no es válido para emitir una factura.",
                    icon: "error",
                });
                return;
            }
            body = mergeComprobanteOnJson(body);

            await fetchAxiosToken({
                url: `/pedidos_pagos_habitaciones/PostPagoByAdminEfectivoOTarjeta/`,
                method: `post`,
                body,
            });

            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Pago cargado con éxito",
                icon: "success",
                confirmButtonText: "Ok",
            });
            navigate("/");
        } catch (e: any) {
            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Error",
                text:
                    e?.response?.data?.message ??
                    "No se pudo registrar el pago.",
                icon: "error",
            });
        } finally {
            setEnviando(false);
        }
    };

    if (!pedido)
        return (
            <>
                <div className="fixed top-0 left-0 right-0 flex items-center justify-between p-3 font-medium text-center border shadow-md px-14 md:px-5">
                    <button
                        onClick={() => navigate("/")}
                        className="hover:underline">
                        Regresar al home
                    </button>
                    <NavbarsAvatar />
                </div>
                <div className="p-4 mt-20 font-medium">Cargando pedido...</div>
            </>
        );

    return (
        <div>
            {modalVisibleLogin && (
                <ModalLogin
                    modalVisibleLogin={modalVisibleLogin}
                    setModalVisibleLogin={setModalVisibleLogin}
                />
            )}

            <div className="flex items-center justify-between p-3 font-medium text-center border shadow-md px-14 md:px-5">
                <button
                    onClick={() => navigate("/")}
                    className="hover:underline">
                    Regresar al home
                </button>
                <NavbarsAvatar />
            </div>

            <div className="flex items-center justify-center p-3 font-medium text-center px-14 md:px-5">
                <p className="mt-4 text-2xl uppercase 2xl:text-4xl">
                    Pagar reserva
                </p>
            </div>

            <div className="grid w-full gap-10 p-4 mx-auto mt-3 2xl:mt-10 sm:grid-cols-2 max-w-7xl">
                <div className="w-full p-4 text-sm border-2 rounded-lg 2xl:text-base">
                    <input
                        type="radio"
                        checked={pagoUnico}
                        onChange={() => setPagoUnico(true)}
                    />
                    <span className="ml-3 font-semibold">Pago único:</span>
                    <p>Realiza el pago total de la reserva.</p>

                    <hr className="my-5 border-t-2 border-gray-300" />

                    {house.dos_pagos && (
                        <div
                            className={
                                pedido.estado === "Pago Parcial" ? "hidden" : ""
                            }>
                            <input
                                type="radio"
                                checked={!pagoUnico}
                                onChange={() => setPagoUnico(false)}
                            />
                            <span className="ml-3 font-semibold">
                                Pago en dos partes:
                            </span>
                            <p>
                                Primer pago ahora y el resto en un próximo pago.
                            </p>
                            <p className="text-sm text-gray-500">
                                Recomendable el {house.porcentaje_primer_pago}%
                                (
                                {formatearNumero(
                                    Math.ceil(
                                        ((isComprobante
                                            ? totalConIgv
                                            : baseTotal) *
                                            Number(
                                                house.porcentaje_primer_pago
                                            )) /
                                            100
                                    )
                                )}{" "}
                                {pedido.tipo_moneda})
                            </p>

                            {!pagoUnico && pedido.estado !== "Pago Parcial" && (
                                <div className="flex items-center gap-2 mt-4">
                                    <span className="font-medium">
                                        Primer pago:
                                    </span>
                                    <input
                                        type="number"
                                        value={montoAPagar ?? undefined}
                                        onChange={(e) =>
                                            setMontoAPagar(
                                                e.target.value
                                                    ? Number(e.target.value)
                                                    : null
                                            )
                                        }
                                        className="w-1/2 max-w-sm 2xl:p-3 p-2 2xl:text-base text-sm  mt-2 text-black border border-[#1C4CBB] rounded-lg placeholder:italic"
                                    />
                                    <div className="text-xl font-bold text-gray-800 ">
                                        {pedido.tipo_moneda}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {mostrarOpcionComprobante && house.is_facturacion && (
                        <>
                            <hr className="my-5 border-t-2 border-gray-300" />
                            <label className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    checked={isComprobante}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        if (checked) {
                                            setShowModalComprobante(true);
                                        } else {
                                            setIsComprobante(false);
                                            setFactData(null);
                                        }
                                    }}
                                />
                                <span className="font-semibold">
                                    Solicitar comprobante (Factura/Boleta)
                                </span>
                            </label>
                            {isComprobante && factData && (
                                <p className="mt-1 text-xs text-gray-600">
                                    {factData.tipo_comprobante} · Doc:{" "}
                                    {factData.tipo_documento_identidad} ·{" "}
                                    {factData.numero_documento_identidad}{" "}
                                    {isIgv ? "· IGV 18%" : "· Sin IGV"}
                                </p>
                            )}
                        </>
                    )}
                </div>

                <div className="flex flex-col w-full p-4 text-sm border-2 rounded-lg 2xl:text-base">
                    <div className="text-base ">
                        Pedido para{" "}
                        <span className="mx-1 font-medium">
                            {pedido.HotelHabitaciones?.nombre || "Tour"}.
                        </span>
                    </div>
                    <span>
                        Desde el{" "}
                        <span className="font-medium">
                            {pedido.fecha_inicio.split("-").reverse().join("-")}
                        </span>
                        , hasta el{" "}
                        <span className="font-medium">
                            {pedido.fecha_final.split("-").reverse().join("-")}
                        </span>
                        .
                    </span>

                    <div className="flex flex-col gap-2">
                        <div className="flex justify-between mt-4">
                            <span className="font-medium">Precio alquiler</span>
                            <span className="font-medium">
                                {formatearNumero(Number(pedido.costo_alquiler))}{" "}
                                {pedido.tipo_moneda}
                            </span>
                        </div>

                        {Number(pedido.descuento_cupon) > 0 && (
                            <>
                                <div className="flex justify-between">
                                    <span className="text-green-500">
                                        Descuento por cupón
                                    </span>
                                    <span className="font-medium text-green-600">
                                        -{" "}
                                        {formatearNumero(
                                            Number(pedido.descuento_cupon)
                                        )}{" "}
                                        {pedido.tipo_moneda}
                                    </span>
                                </div>
                                <hr className="my-1" />
                            </>
                        )}

                        {Number(pedido.garantia) > 0 && (
                            <div className="flex justify-between mt-4">
                                <span className="font-medium">Garantía</span>
                                <span className="font-medium">
                                    {formatearNumero(Number(pedido.garantia))}{" "}
                                    {pedido.tipo_moneda}
                                </span>
                            </div>
                        )}
                        <hr className="my-1" />

                        {Number(pedido.descuento_estadia) > 0 && (
                            <>
                                <div className="flex justify-between">
                                    <span className="text-green-600">
                                        Descuento por larga estadía
                                    </span>
                                    <span className="font-medium text-green-600">
                                        -{" "}
                                        {formatearNumero(
                                            Number(pedido.descuento_estadia)
                                        )}{" "}
                                        {pedido.tipo_moneda}
                                    </span>
                                </div>
                                <hr className="my-1" />
                            </>
                        )}

                        {pedido.pedidos_servicios_adicionales.length > 0 && (
                            <>
                                <span className="font-medium">
                                    Servicios adicionales
                                </span>
                                {pedido.pedidos_servicios_adicionales.map(
                                    (s) => (
                                        <div
                                            key={s.id}
                                            className="flex justify-between">
                                            <span>{s.servicio}</span>
                                            <span className="font-medium">
                                                {formatearNumero(
                                                    Number(s.costo)
                                                )}{" "}
                                                {pedido.tipo_moneda}
                                            </span>
                                        </div>
                                    )
                                )}
                                <hr className="my-1" />
                            </>
                        )}
                        {pedido.pedidos_tours.length > 0 && (
                            <>
                                <span className="font-medium">Tours</span>
                                {pedido.pedidos_tours.map((s) => (
                                    <div
                                        key={s.id}
                                        className="flex justify-between">
                                        <span>
                                            {s.hotel_tour.nombre} x
                                            {s.numero_adultos + s.numero_ninos}{" "}
                                        </span>
                                        <span className="font-medium">
                                            {formatearNumero(
                                                Number(s.precio_tour)
                                            )}{" "}
                                            {pedido.tipo_moneda}
                                        </span>
                                    </div>
                                ))}
                                <hr className="my-1" />
                            </>
                        )}
                    </div>

                    <div className="flex justify-between mt-4">
                        <span className="font-medium">
                            Precio total de la reserva
                        </span>
                        <span className="font-medium">
                            {formatearNumero(baseTotal)} {pedido.tipo_moneda}
                        </span>
                    </div>

                    {isComprobante && (
                        <>
                            <div className="flex justify-between mt-2">
                                <span className="font-medium">
                                    IGV {isIgv ? "(18%)" : "(0%)"}
                                </span>
                                <span className="font-medium">
                                    {formatearNumero(igv)} {pedido.tipo_moneda}
                                </span>
                            </div>

                            <div className="flex justify-between mt-1">
                                <span className="font-semibold">
                                    Total con IGV
                                </span>
                                <span className="font-semibold">
                                    {formatearNumero(totalConIgv)}{" "}
                                    {pedido.tipo_moneda}
                                </span>
                            </div>
                        </>
                    )}
                </div>

                <div className="w-full p-3 border-2 rounded-lg 2xl:p-4">
                    <p className="mt-3 font-semibold">
                        Numero de pedido : {id_reserva}
                    </p>

                    <hr className="my-5 border-t-2 border-gray-300" />

                    <select
                        onChange={(e) => {
                            setFormaPago(e.target.value);
                        }}
                        value={formaPago}
                        className="bg-slate-100 2xl:mt-7 mt-3 2xl:p-3 p-2 max-w-[500px] w-full border-gray-200 border rounded-md cursor-pointer 2xl:text-base text-sm">
                        <option value="Transferencia bancaria">
                            Transferencia bancaria
                        </option>
                        <option className="p-10" value="Tarjeta">
                            {" "}
                            Tarjeta de crédito o débito{" "}
                        </option>{" "}
                        <option className="p-10" value="Efectivo">
                            {" "}
                            Efectivo{" "}
                        </option>
                    </select>

                    {formaPago === "Transferencia bancaria" ? (
                        <div className="text-sm 2xl:text-base">
                            {" "}
                            <h1 className="mt-4 font-semibold">
                                {" "}
                                Por favor, adjuntar el comprobante en formato
                                Imagen{" "}
                            </h1>{" "}
                            <div className="relative flex mx-auto mt-4 bg-white border-4 border-dashed rounded-lg w-72 h-72 ">
                                {" "}
                                {comprobantePreview ? (
                                    <div className="w-full h-full rounded-lg ">
                                        {" "}
                                        <img
                                            className="object-cover w-full h-full rounded-lg"
                                            src={comprobantePreview}
                                            alt="Comprobante de pago"
                                        />{" "}
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center p-3 ">
                                        {" "}
                                        <div className="flex flex-col items-center justify-center w-full mx-auto ">
                                            {" "}
                                            <div className="relative flex flex-col items-center justify-center">
                                                {" "}
                                                <span className="mt-2 font-bold text-center ">
                                                    {" "}
                                                    Arrastre el comprobante{" "}
                                                </span>{" "}
                                                o{" "}
                                                <span className="font-normal text-center text-blue-500">
                                                    {" "}
                                                    Sube la imagen desde tu
                                                    dispositivo{" "}
                                                </span>{" "}
                                            </div>{" "}
                                        </div>{" "}
                                    </div>
                                )}{" "}
                                <input
                                    type="file"
                                    onChange={handleComprobante}
                                    accept="image/*"
                                    className="absolute top-0 left-0 opacity-0 cursor-pointer w-72 h-72 file:cursor-pointer"
                                />{" "}
                            </div>{" "}
                            <button
                                disabled={enviando}
                                onClick={sendPayment}
                                className="flex justify-center w-full max-w-xs p-2 mx-auto mt-4 border-2 rounded-lg hover:bg-slate-100 hover:scale-105 disabled:bg-slate-100">
                                {" "}
                                {enviando ? "Enviando.." : "Pagar"}{" "}
                            </button>{" "}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center mt-4">
                            {" "}
                            <button
                                disabled={enviando}
                                onClick={() =>
                                    sendPaymentPorEfetivoOTarjeta(
                                        formaPago === "Efectivo"
                                            ? "Efectivo"
                                            : "Tarjeta"
                                    )
                                }
                                className="flex justify-center w-full max-w-xs p-2 mx-auto mt-4 border-2 rounded-lg hover:bg-slate-100 hover:scale-105 disabled:bg-slate-100">
                                {" "}
                                {enviando ? "Enviando.." : "Pagar"}{" "}
                            </button>{" "}
                        </div>
                    )}
                </div>

                <div className="flex flex-col w-full p-4 text-sm border-2 rounded-lg h-60 2xl:text-base">
                    <div className="text-base font-semibold ">Resumen</div>

                    <div className="flex flex-col gap-2">
                        <div className="flex justify-between mt-4">
                            <span className="font-medium">Pagado</span>
                            <span className="font-medium">
                                {formatearNumero(Number(pedido.pagado))}{" "}
                                {pedido.tipo_moneda}
                            </span>
                        </div>
                        <hr className="my-1" />
                    </div>

                    <div className="flex flex-col gap-2">
                        <div className="flex justify-between mt-4">
                            <span className="font-medium">Monto a pagar </span>
                            {pedido.estado === "Pago Parcial" ? (
                                <span className="font-medium">
                                    {formatearNumero(Number(pedido.saldo))}{" "}
                                    {pedido.tipo_moneda}
                                </span>
                            ) : pagoUnico ? (
                                <span className="font-medium">
                                    {formatearNumero(
                                        isComprobante ? totalConIgv : baseTotal
                                    )}{" "}
                                    {pedido.tipo_moneda}
                                </span>
                            ) : (
                                <span className="font-medium">
                                    {montoAPagar
                                        ? formatearNumero(montoAPagar)
                                        : "0"}{" "}
                                    {pedido.tipo_moneda}
                                </span>
                            )}
                        </div>
                        <hr className="my-1" />
                    </div>

                    <div className="flex justify-between mt-4">
                        <span className="font-medium">Saldo pendiente</span>
                        {pagoUnico ? (
                            <span className="font-medium">
                                0 {pedido.tipo_moneda}
                            </span>
                        ) : (
                            <span className="font-medium">
                                {formatearNumero(
                                    Number(pedido.saldo) +
                                        (isComprobante ? igv : 0) -
                                        (montoAPagar ? montoAPagar : 0)
                                )}{" "}
                                {pedido.tipo_moneda}
                            </span>
                        )}
                    </div>

                    <div className="flex justify-between mt-4">
                        {pedido.garantia !== "0.00" &&
                            pedido.tipo_garantia === "Pago al ingreso casa" && (
                                <div className="flex w-full px-1 mt-4 bg-blue-200 border-4 border-blue-300 rounded-lg">
                                    <span className="underline">
                                        Garantía:{" "}
                                    </span>
                                    <span className="ml-2 italic font-thin ">
                                        Deberás pagar{" "}
                                        {formatearNumero(
                                            Number(pedido.garantia)
                                        )}{" "}
                                        {pedido.tipo_moneda} en efectivo al
                                        momento de llegar a la propiedad
                                    </span>
                                </div>
                            )}
                    </div>
                </div>
            </div>

            <ModalDatosComprobante
                visible={showModalComprobante}
                onClose={() => {
                    setShowModalComprobante(false);
                    if (!factData) setIsComprobante(false);
                }}
                initial={factData ?? undefined}
                onSave={(data) => {
                    setFactData(data);
                    setIsComprobante(true);
                    setShowModalComprobante(false);
                }}
            />
        </div>
    );
};
